import React, { useEffect, useState } from "react";
import {
  ADD_LABEL,
  PERFORMANCE_COMMENT_LABEL,
  EMPTY_ADMIN_COMMENT_MESSAGE,
  EMPTY_DEVELOPMENT_COMMENT_MESSAGE,
  DEVELOPMENT_COMMENTS_LABEL,
  SEND_COMMENT_LABEL,
  SEND_COMMENT_DEVELOPMENT_MODAL_TITLE,
  SEND_COMMENT_DEVELOPMENT_MODAL_MESSAGE,
  COMMENT_SENT_DEVELOPMENT_SECTION_LABEL,
  SUBMITTED_COMMENT_DEVELOPMENT_LABEL,
} from "../../../../../constants/Dictionary";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "../../../../../components/core/FormField/FormField";
import {
  StyledBodyAdminComment,
  StyledDevelopmentCommentWrapper,
  StyledMessageChipWrapper,
  StyledSendCommentRow,
  StyledWrapperCommentInfo,
} from "./AdminComent.styled";
import TextArea from "../../../../../components/core/TextArea/TextArea";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { UpdateCmSheetAdminFeedbacksRequest } from "../../../../../types/api/collaborators.type";
import hrComment from "../../../../../images/hrComment.png";
import { ReactComponent as AddIcon } from "../../../../../icons/add.svg";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { TextBox } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import {
  CHARACTER_MAX_LENGTH,
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { ReactComponent as InfoIcon } from "../../../../../icons/info.svg";
import { ReactComponent as SendIcon } from "../../../../../icons/send.svg";
import { theme } from "../../../../../constants/StyledComponents";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import MessageChip from "../../../../../components/MessageChip/MessageChip";
import { ReactComponent as CheckIcon } from "../../../../../icons/check.svg";
import {
  useCmSheetAdminFeedbacks,
  useSendAdminComments,
  useUpdateCmSheetAdminFeedbacks,
} from "../../../../../hooks/cmSheet/adminComments.hook";

type FormData = {
  developmentComment?: string;
  performanceComment?: string;
};

export const AdminComment = ({
  footer,
  cmSheetInfo,
  isMyProfile,
  cmSheetStateMachineInfo,
  showFooter,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { profile } = useAppSelector((state) => state.user);

  const [hasData, setHasData] = useState<boolean | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { data, isFetching } = useCmSheetAdminFeedbacks(cmSheetInfo.id);
  const { mutateAsync: updateAdminFeedbacks, isPending: isUpdating } =
    useUpdateCmSheetAdminFeedbacks(cmSheetInfo.id);
  const { mutateAsync: sendAdminComments, isPending: isSendingAdminComment } =
    useSendAdminComments(cmSheetInfo.id);

  const commentSent = !!data?.isDevelopmentCommentSent;

  const schema = yup
    .object({
      developmentComment: yup.string(),
      performanceComment: yup.string(),
    })
    .required();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const body: UpdateCmSheetAdminFeedbacksRequest = {
        developmentComment: data.developmentComment ?? "",
        performanceComment: data.performanceComment ?? "",
      };
      await updateAdminFeedbacks(body);
    } finally {
      setEdit(false);
    }
  };

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      reset({
        developmentComment: data?.developmentComment ?? "",
        performanceComment: data?.performanceComment ?? "",
      });

      if (data && hasData === null) {
        const newHasData = !!(
          data.developmentComment || data.performanceComment
        );
        setHasData(newHasData);
      }
    }
  }, [data, hasData, edit, isFetching]);

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="adminComment"
        disabledEditButton={!isDirty || isUpdating}
        edit={edit}
        showEdit={!!hasData || edit}
        setEdit={setEdit}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
        showFooter={showFooter}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    isUpdating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  const getTitleEmptyState = () => {
    if (!showFooter) {
      return "";
    }

    if (roleHasPermission(profile, PermissionsEnum.EDIT_ADMIN_COMMENT)) {
      return EMPTY_ADMIN_COMMENT_MESSAGE;
    }

    return EMPTY_DEVELOPMENT_COMMENT_MESSAGE;
  };

  const handleConfirmSendDevelopmentComment = async () => {
    await sendAdminComments();
    setIsConfirmModalOpen(false);
  };

  const sendDevelopmentCommentIsEnabled = () => {
    if (
      !commentSent &&
      data?.developmentComment &&
      cmSheetStateMachineInfo.currentState ===
        CmSheetStateEnum.INTERMEDIATE_EVALUATION &&
      !cmSheetStateMachineInfo.isRevisionRequestSent
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState label={getTitleEmptyState()} imageSrc={hrComment}>
            {showFooter && (
              <Button
                label={ADD_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<AddIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={isUpdating}>
          {/* Edit mode */}
          <StyledHideOrShowContainer $show={edit}>
            <StyledBodyAdminComment>
              <form id="adminComment" onSubmit={handleSubmit(onSubmit)}>
                {commentSent ? (
                  <StyledDevelopmentCommentWrapper>
                    <TextBox
                      label={DEVELOPMENT_COMMENTS_LABEL}
                      text={data?.developmentComment}
                    />
                    <StyledMessageChipWrapper>
                      <MessageChip
                        message={SUBMITTED_COMMENT_DEVELOPMENT_LABEL}
                        color={theme.colors.purple_2}
                        icon={<CheckIcon />}
                      />
                    </StyledMessageChipWrapper>
                  </StyledDevelopmentCommentWrapper>
                ) : (
                  <FormField error={errors.developmentComment?.message}>
                    <Controller
                      name="developmentComment"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label={DEVELOPMENT_COMMENTS_LABEL}
                          maxLength={CHARACTER_MAX_LENGTH}
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                )}
                <StyledHideOrShowContainer
                  $show={roleHasPermission(
                    profile,
                    PermissionsEnum.EDIT_ADMIN_COMMENT,
                  )}
                >
                  <FormField error={errors.performanceComment?.message}>
                    <Controller
                      name="performanceComment"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label={PERFORMANCE_COMMENT_LABEL}
                          maxLength={CHARACTER_MAX_LENGTH}
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </StyledHideOrShowContainer>
              </form>
            </StyledBodyAdminComment>
          </StyledHideOrShowContainer>
          {/* Read mode */}
          <StyledHideOrShowContainer $show={!edit}>
            <StyledBodyAdminComment>
              <StyledDevelopmentCommentWrapper>
                <TextBox
                  label={DEVELOPMENT_COMMENTS_LABEL}
                  text={data?.developmentComment}
                />
                {commentSent ? (
                  <StyledMessageChipWrapper>
                    <MessageChip
                      message={SUBMITTED_COMMENT_DEVELOPMENT_LABEL}
                      color={theme.colors.purple_2}
                      icon={<CheckIcon />}
                    />
                  </StyledMessageChipWrapper>
                ) : (
                  <StyledHideOrShowContainer
                    $show={roleHasPermission(
                      profile,
                      PermissionsEnum.EDIT_ADMIN_COMMENT,
                    )}
                  >
                    <StyledSendCommentRow>
                      <StyledWrapperCommentInfo>
                        <IconManager
                          size={IconSizeEnum.MEDIUM}
                          color={theme.colors.purple}
                        >
                          <InfoIcon />
                        </IconManager>
                        <BodyTyp variant="3" fontWeight={FontWeightEnum.MEDIUM}>
                          {COMMENT_SENT_DEVELOPMENT_SECTION_LABEL}
                        </BodyTyp>
                      </StyledWrapperCommentInfo>

                      <Button
                        label={SEND_COMMENT_LABEL}
                        variant={ButtonVariants.PRIMARY}
                        size={ButtonSizes.SMALL}
                        icon={<SendIcon />}
                        onClick={() => setIsConfirmModalOpen(true)}
                        disabled={!sendDevelopmentCommentIsEnabled()}
                      />
                    </StyledSendCommentRow>
                  </StyledHideOrShowContainer>
                )}
              </StyledDevelopmentCommentWrapper>
              <TextBox
                label={PERFORMANCE_COMMENT_LABEL}
                text={data?.performanceComment}
              />
            </StyledBodyAdminComment>
          </StyledHideOrShowContainer>
        </Loader>
      </StyledHideOrShowContainer>
      <PopupConfirm
        isOpen={isConfirmModalOpen}
        label={SEND_COMMENT_DEVELOPMENT_MODAL_TITLE}
        popupText={SEND_COMMENT_DEVELOPMENT_MODAL_MESSAGE}
        onConfirm={handleConfirmSendDevelopmentComment}
        onCancel={() => setIsConfirmModalOpen(false)}
        isLoading={isSendingAdminComment}
      />
    </>
  );
};
